import { zodResolver } from '@hookform/resolvers/zod'
import Box from '@repo/ui/box'
import CapsuleTab, { Tab } from '@repo/ui/capsule-tab'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { canGoOutAloneOption } from '../../../../api/enrollments/can-go-out-alone-option'
import { getEnrollment } from '../../../../api/enrollments/get-enrollment'
import { getEnrollmentFriends } from '../../../../api/enrollments/get-enrollment-friends'
import { updateOtoIndividualProposition } from '../../../../api/enrollments/update-oto-individual-proposition'
import { useObserver } from '../../../../lib/contexts/observer'
import { useEnrollmentStatus } from '../../../../lib/hooks/use-enrollment-status'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { useSections } from '../../../../lib/hooks/use-sections'
import useTranslate from '../../../../lib/hooks/use-translate'
import { enrollmentUpdateFormFields } from '../../../../lib/types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import {
  mapOtoIndividualPropositionInputToRequest,
  mapOtoIndividualPropositionToInput,
  OtoIndividualPropositionInput
} from '../../../../lib/types/models/enrollment-proposition/oto-individual-proposition-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form } from '../../../elements/forms/form'
import LoadingScreen from '../../../elements/loading/loading-screen'
import ValidationButtons from '../../../elements/validation-buttons'
import EnrollmentPropositionInsuranceModal from '../enrollment-proposition-insurance-modal'
import OtoIndividualPropositionFormModal from '../enrolment-create-proposition-steps/oto-individual-proposition-form-modal'
import OtoIndividualPropositionSummary from '../enrolment-create-proposition-steps/oto-individual-proposition-summary'
import { ENROLLMENT_SCHEMA, mapEnrollmentToEnrollmentInput } from '../utils/enrollments-utils'
import EnrollmentComments from './enrollment-comments'
import { EnrollmentFormHeader } from './enrollment-form-header'
import EnrollmentInformationForm from './enrollment-information-form'
import EnrollmentPaymentRecord from './enrollment-payment-record'
import EnrollmentPropositionManager from './enrollment-proposition-manager'

type Props = BaseFormProps<EnrollmentInput> & {
  enrollmentId: number
}

const tabs: Tab<'required-key'>[] = [
  { label: 'Séjours', key: 'enrollment-trips' },
  { label: 'Informations', key: 'enrollment-informations' },
  { label: 'Paiements', key: 'enrollment-payments' },
  { label: 'Commentaires', key: 'enrollment-comments' }
]

const UpdateEnrollmentForm = ({ enrollmentId, ...validationButtonProps }: Props) => {
  const { t } = useTranslate()
  const { active, handleChange } = useSections({ sections: tabs })

  const { data: friends, refetch: refetchFriends } = useQuery({
    queryKey: ['enrollments-friends', enrollmentId],
    queryFn: () => getEnrollmentFriends(enrollmentId),
    enabled: !!enrollmentId
  })

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['enrollment', enrollmentId, enrollmentUpdateFormFields],
    queryFn: () => getEnrollment(enrollmentId, enrollmentUpdateFormFields)
  })

  const { data: enrollmentStatus, refetch: refetchEnrollmentStatus } =
    useEnrollmentStatus(enrollmentId)

  const { notify } = useObserver({
    key: 'enrollment-form-update',
    update: refetch
  })

  useObserver({
    key: 'enrollment-status',
    update: refetchEnrollmentStatus
  })

  const form = useForm<EnrollmentInput>({
    resolver: zodResolver(ENROLLMENT_SCHEMA(t)),
    mode: FORM_MODE
  })

  // selectedData holds the tab index the OTO proposition modal opens on
  const {
    modalState,
    toggleModal,
    closeModel,
    selectedData: otoModalIndex
  } = useModalManager<'edit-oto' | 'oto-insurance', number>(['edit-oto', 'oto-insurance'])
  const [otoProposition, setOtoProposition] = useState<OtoIndividualPropositionInput>()

  const { mutateAsync: updateOtoIndividualPropositionMutation } = useMutation({
    mutationFn: updateOtoIndividualProposition,
    onError: showAxiosError,
    onSuccess: () => toast.success('Proposition One to One Individuel modifiée avec succès')
  })

  // the OTO Individual proposition is persisted immediately, like on the devis page
  const handleOtoPropositionChange = async (values: OtoIndividualPropositionInput) => {
    await updateOtoIndividualPropositionMutation({
      id: enrollmentId,
      ...mapOtoIndividualPropositionInputToRequest(values)
    })
    setOtoProposition(values)
    notify(['enrollment-payment-record', 'enrollment-status', 'enrollment-overpayment'])
    refetch()
  }

  const childBirthDate = form.watch('childBirthDate')

  // the country and formula come from whichever proposition the enrollment carries
  const countryCode = EnrollmentProperties.getEnrollmentCountry(data).value
  const formulaId = EnrollmentProperties.isOtoIndividual(data)
    ? data?.otoIndividualProposition?.product.formula.id
    : data?.mainProposition?.journey.date.product.formula.id
  // the rights table applies to the age of the child during the stay
  const stayDates = EnrollmentProperties.getEnrollmentDepartureAndArrivalDates(data).value

  const { data: canGoOutAloneOptionData } = useQuery({
    queryKey: [
      'can-go-out-alone-option',
      enrollmentId,
      countryCode,
      formulaId,
      stayDates?.startDate,
      stayDates?.endDate,
      childBirthDate
    ],
    queryFn: () => {
      if (childBirthDate && stayDates && countryCode && formulaId)
        return canGoOutAloneOption({
          childBirthDate,
          departureDate: stayDates.startDate,
          returnDate: stayDates.endDate,
          countryCode,
          formulaId
        })
    },
    enabled: !!data && !!childBirthDate
  })

  useEffect(() => {
    if (data) {
      form.reset(mapEnrollmentToEnrollmentInput(data))
      setOtoProposition(
        data.otoIndividualProposition
          ? mapOtoIndividualPropositionToInput(data.otoIndividualProposition)
          : undefined
      )
      refetchFriends().then((res) => {
        form.setValue('declaredFriends', res.data?.data)
      })
    }
  }, [data])

  useEffect(() => {
    if (friends) form.setValue('declaredFriends', friends.data)
  }, [friends])

  if (isLoading || !data) return <LoadingScreen />

  return (
    <div>
      <Box className="p-5 flex flex-col gap-6">
        {/* Header */}
        <EnrollmentFormHeader
          enrollment={data}
          enrollmentStatus={enrollmentStatus}
          mode={'detailed'}
        />

        {/* trip */}
        <div className="sticky top-2 z-30">
          <CapsuleTab
            variant="secondary"
            onTabChange={handleChange}
            tabs={tabs}
            activeTab={active}
          />
        </div>

        {EnrollmentProperties.isOtoIndividual(data) ? (
          // never mount the proposition manager for an OTO Individual enrollment: it has no
          // journey propositions, so every proposition call would 404
          otoProposition && (
            <OtoIndividualPropositionSummary
              input={otoProposition}
              childBirthDate={childBirthDate}
              onEdit={(tabIndex) => toggleModal('edit-oto', tabIndex)}
              onInputChange={handleOtoPropositionChange}
              onEditInsurances={() => toggleModal('oto-insurance')}
            />
          )
        ) : (
          <EnrollmentPropositionManager
            enrollmentId={data.id}
            onSuccess={() => {
              notify([
                'enrollment-payment-record',
                'enrollment-profile-monitoring-payment-record',
                'enrollment-overpayment',
                'enrollment-status'
              ])
              refetch()
            }}
          />
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
            <EnrollmentInformationForm
              mode="edit"
              enrollmentId={data.id}
              canGoOutAloneOption={!!canGoOutAloneOptionData}
              canEditMainMedicalInformations={data.canEditMainMedicalInformations === true}
              departureDate={
                EnrollmentProperties.getEnrollmentDepartureAndArrivalDates(data).value?.startDate
              }
            />
            <ValidationButtons {...validationButtonProps} />
          </form>
        </Form>

        {enrollmentStatus?.hasPayment && <EnrollmentPaymentRecord enrollmentId={enrollmentId} />}

        <EnrollmentComments enrollmentId={enrollmentId} />
      </Box>

      <OtoIndividualPropositionFormModal
        isOpen={modalState['edit-oto']}
        defaultIndex={otoModalIndex ?? 0}
        defaultValues={otoProposition}
        childBirthDate={childBirthDate}
        onClose={() => closeModel('edit-oto')}
        handleSubmit={async (values) => {
          await handleOtoPropositionChange(values)
          closeModel('edit-oto')
        }}
      />

      <EnrollmentPropositionInsuranceModal
        isOpen={modalState['oto-insurance']}
        residenceCountry={data.country ?? undefined}
        destinationCountry={data.otoIndividualProposition?.product.country.id}
        defaultValues={otoProposition?.insurances}
        onClose={() => closeModel('oto-insurance')}
        onSubmit={async (insurances) => {
          if (!otoProposition) return
          await handleOtoPropositionChange({ ...otoProposition, insurances })
          closeModel('oto-insurance')
        }}
      />
    </div>
  )
}

export default UpdateEnrollmentForm
